import { createFileRoute } from "@tanstack/react-router";
import { useConvexAuth, useMutation, useQuery } from "convex/react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "#/../convex/_generated/api";
import { CatalogueTab } from "#/components/admin/CatalogueTab";
import { DiscModelsTab } from "#/components/admin/DiscModelsTab";
import { FeedbackTab } from "#/components/admin/FeedbackTab";
import { ManufacturersTab } from "#/components/admin/ManufacturersTab";
import { PlasticsTab } from "#/components/admin/PlasticsTab";
import { ReportsTab } from "#/components/admin/ReportsTab";
import { Badge } from "#/components/ui/badge";
import { Button } from "#/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "#/components/ui/tabs";
import { useSignInModal } from "#/hooks/useSignInModal";
import { toast } from "#/lib/toast";

export const Route = createFileRoute("/admin")({
	component: AdminPage,
});

function AdminPage() {
	const { isLoading, isAuthenticated } = useConvexAuth();
	const isAdmin = useQuery(api.adminHelpers.getIsAdmin);
	const { open } = useSignInModal();
	useEffect(() => {
		if (!isLoading && !isAuthenticated) open();
	}, [isLoading, isAuthenticated, open]);

	if (isLoading) return null;
	if (!isAuthenticated) return null;
	if (isAdmin === undefined) return null;
	if (!isAdmin) return <NotAuthorized />;

	return <AdminContent />;
}

function NotAuthorized() {
	const { t } = useTranslation("admin");
	return (
		<div className="page-wrap py-12 text-center">
			<p className="text-muted-foreground">{t("notAuthorized")}</p>
		</div>
	);
}

function AdminContent() {
	const { t } = useTranslation("admin");
	const manufacturers = useQuery(api.manufacturers.list) ?? [];
	const discModels = useQuery(api.discModels.list) ?? [];
	const plastics = useQuery(api.plastics.listAll) ?? [];
	const openReports = useQuery(api.listingReports.listOpen) ?? [];
	const openFeedback = useQuery(api.appReports.listOpen) ?? [];
	const unresolvedCount = openReports.length;
	const unreadFeedbackCount = openFeedback.length;
	const runBackfill = useMutation(api.listings.runBackfillSearchText);
	const [backfillPending, setBackfillPending] = useState(false);

	async function handleBackfill() {
		setBackfillPending(true);
		try {
			const result = await runBackfill({});
			toast.success(t("maintenance.success", { count: result.updated }));
		} catch (err) {
			toast.error(err instanceof Error ? err.message : t("maintenance.error"));
		} finally {
			setBackfillPending(false);
		}
	}

	return (
		<div className="page-wrap py-12">
			<div className="mx-auto max-w-6xl flex flex-col gap-8">
				<div>
					<p className="mb-1 text-xs font-bold uppercase tracking-widest text-muted-foreground">
						{t("header.tag")}
					</p>
					<h1
						className="text-4xl font-bold text-foreground leading-tight"
						style={{ fontFamily: "Fraunces, serif" }}
					>
						{t("header.title")}
					</h1>
				</div>

				<div className="flex flex-col gap-2">
					<p className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
						{t("maintenance.title")}
					</p>
					<div className="flex items-center gap-3">
						<Button
							variant="outline"
							size="sm"
							onClick={handleBackfill}
							disabled={backfillPending}
						>
							{backfillPending
								? t("maintenance.running")
								: t("maintenance.backfill")}
						</Button>
						<span className="text-xs text-muted-foreground">
							{t("maintenance.description")}
						</span>
					</div>
				</div>

				<Tabs defaultValue="manufacturers">
					<TabsList>
						<TabsTrigger value="manufacturers">
							{t("tabs.manufacturers")} ({manufacturers.length})
						</TabsTrigger>
						<TabsTrigger value="disc-models">
							{t("tabs.discModels")} ({discModels.length})
						</TabsTrigger>
						<TabsTrigger value="plastics">
							{t("tabs.plastics")} ({plastics.length})
						</TabsTrigger>
						<TabsTrigger value="reports" className="gap-1.5">
							{t("tabs.reports")}
							{unresolvedCount > 0 && (
								<Badge
									variant="destructive"
									className="text-xs px-1.5 py-0 h-4"
								>
									{unresolvedCount}
								</Badge>
							)}
						</TabsTrigger>
						<TabsTrigger value="feedback" className="gap-1.5">
							{t("tabs.feedback")}
							{unreadFeedbackCount > 0 && (
								<Badge
									variant="destructive"
									className="text-xs px-1.5 py-0 h-4"
								>
									{unreadFeedbackCount}
								</Badge>
							)}
						</TabsTrigger>
						<TabsTrigger value="catalogue">{t("tabs.catalogue")}</TabsTrigger>
					</TabsList>

					<TabsContent value="manufacturers" className="mt-6">
						<ManufacturersTab manufacturers={manufacturers} />
					</TabsContent>

					<TabsContent value="disc-models" className="mt-6">
						<DiscModelsTab
							discModels={discModels}
							manufacturers={manufacturers}
						/>
					</TabsContent>

					<TabsContent value="plastics" className="mt-6">
						<PlasticsTab plastics={plastics} manufacturers={manufacturers} />
					</TabsContent>

					<TabsContent value="reports" className="mt-6">
						<ReportsTab />
					</TabsContent>

					<TabsContent value="feedback" className="mt-6">
						<FeedbackTab />
					</TabsContent>

					<TabsContent value="catalogue" className="mt-6">
						<CatalogueTab
							manufacturers={manufacturers}
							discModels={discModels}
							plastics={plastics}
						/>
					</TabsContent>
				</Tabs>
			</div>
		</div>
	);
}
